<template>
    <div class="flex flex-col h-[calc(100vh-1rem)] overflow-hidden">
        <div class="page-header">
            <AppHeader :show-title="false" :gap="''">
                <template #end>
                    <RowActionMenu
                        v-can-access.permissions="[$Permissions.WRITE_TOPICS]"
                        :show-delete="true"
                        :loading="topicLoading || configLoading"
                        @delete="onDeleteTopic('topic', topic.name, topic.id)" />
                </template>
                <template #titleRight>
                    <nav class="text-gray-700" aria-label="Breadcrumb">
                        <ol class="list-none p-0 inline-flex font-semibold">
                            <li class="flex items-center">
                                <router-link to="/topics" class="text-green-500 hover:text-cyan-900">Topics</router-link>
                                <ChevronRightIcon class="w-4 h-4 mx-2 text-cyan-950" />
                                <div class="flex flex-row align-center items-center gap-3">
                                    <h3 class="text-base font-semibold text-cyan-950 flex flex-col items-left" :title="topic?.name">
                                        <span class="truncate" style="max-width: 25rem"> {{ topic?.name || 'Loading...' }} </span>
                                        <span v-if="topic?.id" class="text-xs text-gray-500">ID: {{ topic?.id }}</span>
                                    </h3>
                                </div>
                            </li>
                        </ol>
                    </nav>
                </template>
            </AppHeader>
        </div>

        <!-- Stats Section -->
        <div class="flex-1 overflow-auto">
            <Tabs :tabs="tabs" :initial-active-tab="currentTab" @tab-change="setCurrentTab">
                <template #Status>
                    <div class="mt-3 grid grid-cols-1 gap-5 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-4 mb-10">
                        <StatCard
                            v-for="stat in stats"
                            :title="stat.name"
                            :value="stat.stat"
                            :icon="stat.icon"
                            :icon-bg-class="stat.colorClass"
                            :loading="topicLoading || configLoading || topicMetricsLoading" />
                    </div>

                    <!-- Search and Filters -->
                    <div class="w-full flex justify-between" style="min-height: 60px !important">
                        <div class="flex gap-2">
                            <div class="flex items-center">
                                <button
                                    @click="openFilterModal"
                                    type="button"
                                    :class="
                                        [
                                            messagesLoading ? 'bg-gray-100 opacity-[0.7]' : 'bg-white hover:bg-gray-50',
                                            'px-4 inline-flex justify-center items-center rounded-md border border-gray-300 text-sm font-medium text-gray-700 shadow-xs'
                                        ].join(' ')
                                    "
                                    :style="{ 'min-height': '40.5px' }"
                                    :disabled="messagesLoading">
                                    <FunnelIcon v-if="!hasSelection" class="h-4 w-4 self-center mr-1" />
                                    Filter Messages
                                    <span
                                        class="ml-2 inline-flex items-center bg-blue-100 text-blue-800 text-xs font-semibold rounded-full px-2 py-0.5">
                                        Beta
                                    </span>
                                </button>

                                <div
                                    v-if="
                                        (topicFilter?.regex && topicFilter?.regex?.pattern && topicFilter?.regex?.pattern?.trim() !== '') ||
                                        (topicFilter?.seek?.seekType && topicFilter?.seek?.seekValue !== null)
                                    "
                                    class="ml-[-0.15rem] flex items-center bg-blue-100 font-medium text-blue-800 text-xs px-2 py-1 rounded-r-md"
                                    style="min-height: 40.5px; z-index: 9">
                                    <span class="mr-2">Filter applied</span>
                                    <Tooltip :tooltip-text="filterTooltipText" position="bottom" />
                                    <XMarkIcon class="w-5 h-5 ml-2 cursor-pointer text-blue-800 hover:text-blue-600" @click="removeFilter" />
                                </div>
                                <span
                                    v-if="partitionTotal?.displayValue"
                                    class="ml-2 inline-flex items-center bg-blue-100 text-blue-800 text-sm font-semibold rounded-full px-2 py-0.5">
                                    {{ partitionTotal?.displayValue }} partition(s)
                                </span>
                            </div>
                        </div>
                        <div class="flex items-center justify-end gap-5">
                            <BasicDropdown
                                class="justify-end"
                                :selected="sortingSelected"
                                :options="sortingOptions"
                                :loading="messagesLoading"
                                :placeholder="'Sort by'"
                                @input="
                                    sortingSelected = $event;
                                    messagesPage = 0;
                                    lastOffset = null;
                                    paginationDirection = null;

                                    fetchTopicsMessages(null, null, null, $event);
                                ">
                            </BasicDropdown>
                            <DropdownFilters
                                v-model="checkedFilters"
                                slim-dropdown-button
                                :filters="filters"
                                :disabled="messagesLoading"
                                @on-filter-toggle="onMessagesFilterToggle" />
                        </div>
                    </div>

                    <!-- Chart for Topic Data -->
                    <!-- <div class="bg-white p-4 rounded-lg shadow-sm mb-6">
                        <div class="flex gap-2 items-center">
                            <ChevronUpIcon
                                v-if="showChart"
                                class="h-5 w-5 text-gray-600 cursor-pointer hover:text-gray-800"
                                @click="showChart = !showChart" />
                            <ChevronDownIcon
                                v-else
                                class="h-5 w-5 text-gray-600 cursor-pointer hover:text-gray-800"
                                @click="showChart = !showChart" />
                            <h3 v-if="!showChart">{{ chartOptions.title.text }}</h3>
                        </div>
                        <HighChart v-if="showChart" :chart-options="chartOptions" />
                    </div> -->

                    <TopicMessagesTable
                        :messages="messages"
                        :page-size="topicsStore.messagesPageSize"
                        :unselect-all="!selectedMessage"
                        :page="messagesPage"
                        :sort="sortingSelected"
                        @select-message="selectMessage"
                        @page-change="({ page, direction }) => fetchTopicsMessages(page, null, direction, null)"
                        :loading="messagesLoading"
                        :has-next="topicsStore.messagesHasNext"
                        :total-messages="topicsStore.messagesPageTotal" />

                    <!-- Side Popup for Selected Message -->
                    <TopicMessageDetailsDrawer :message="selectedMessage" @close="selectMessage(null)" />

                    <TopicMessagesFilterModal
                        v-if="showFilterModal"
                        :isOpen="showFilterModal"
                        :initialFilter="topicFilter"
                        @apply="applyFilter"
                        @update:isOpen="
                            (val) => {
                                showFilterModal = val;
                            }
                        " />

                    <!-- Tab Navigation -->
                    <!-- <Tabs :tabs="tabs" @tab-change="setCurrentTab">

               <template #Schema>
                    <TopicColumnSchema :columns="topic?.columns" @hash="onChangeHashColumnState" @exclude="onChangeExcludeColumnState" />
                </template>
            </Tabs> -->
                </template>
                <template #Settings>
                    <AppLoader :listen="['topics.getById', 'metrics.topics.fetch.table']" default-height="45vh">
                        <div class="mt-5 bg-white p-4 shadow-sm sm:rounded-lg overflow-y-auto flex flex-row justify-center w-full">
                            <div class="p-6 space-y-6">
                                <!-- Slider to increase partition count -->
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 mb-1">
                                        Partition Count (Can't be lower than the current count)
                                    </label>
                                    <Slider
                                        v-model="newPartitionCount"
                                        :min="partitionTotal.raw"
                                        :max="100"
                                        :step="1"
                                        :edit-mode="true"
                                        :show-current-value="true" />
                                </div>

                                <!-- Warning Message -->
                                <div class="bg-yellow-50 border-l-4 border-yellow-400 text-yellow-700 p-4 rounded-md">
                                    <div class="flex items-center">
                                        <ExclamationTriangleIcon class="size-5 text-yellow-400" aria-hidden="true" />
                                        <span class="font-semibold">WARNING</span>
                                    </div>
                                    <p class="mt-2 text-sm">
                                        Increasing the number of partitions means records will be sent to different partitions based on their primary
                                        key. This will cause destinations/consumers to read messages out of order. Increasing the number of partitions
                                        should be done using the following procedure
                                    </p>
                                    <ul class="list-disc list-inside mt-2 text-sm">
                                        <li>Stop the sources/producers</li>
                                        <li>Wait for destination/consumer lag to drop to 0</li>
                                        <li>Increase the topic partitions</li>
                                        <li>Start the sources/producers</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </AppLoader>
                </template>
            </Tabs>
        </div>
        <div class="mt-auto">
            <AppFooter v-if="topic" v-can-access.permissions="[$Permissions.WRITE_TOPICS]">
                <template #right>
                    <RowActionMenu
                        :show-save="true"
                        :disable-save="!newPartitionCount || partitionTotal.raw >= newPartitionCount"
                        :loading="topicLoading || configLoading || saveLoading"
                        @save="saveTopic"
                        @discard="onDiscard" />
                </template>
            </AppFooter>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, computed, isRef, watch } from 'vue';
import {
    ExclamationCircleIcon,
    ChevronDoubleLeftIcon,
    ScaleIcon,
    QueueListIcon,
    ChevronRightIcon,
    InformationCircleIcon,
    ChevronDownIcon,
    XMarkIcon
} from '@heroicons/vue/24/outline';
import { FunnelIcon, ExclamationTriangleIcon } from '@heroicons/vue/20/solid';
import AppHeader from '../../components/AppHeader.vue';
import Slider from '@/components/Slider.vue';
import AppFooter from '../../components/AppFooter.vue';
import HighChart from '../../components/HighChart.vue';
import StatCard from '../../components/global/StatCard.vue';
import TopicMessagesTable from '../../components/Topics/TopicMessagesTable.vue';
import TopicMessageDetailsDrawer from '../../components/Topics/TopicMessageDetailsDrawer.vue';
import { useRoute, useRouter } from 'vue-router';
import { useTopicsStore } from '../../stores/topics.store';
import SearchInput from '@/components/global/SearchInput.vue';
import DropdownFilters from '@/components/DropdownFilters.vue';
import TopicMessagesFilterModal from '@/components/global/modals/TopicMessagesFilterModal.vue';
import DateFilter from '@/components/global/DateFilter.vue';
import RowActionMenu from '../../components/RowActionMenu.vue';
import TopicColumnSchema from '@/components/Topics/TopicColumnSchema.vue';
import Tabs from '@/components/global/Tabs.vue';
import { formatter, getMetricFormattingInfo } from '../../utils/utils';
import BasicDropdown from '@/components/BasicDropdown.vue';
import _ from 'lodash';
import { useLoaderStore } from '@/stores/loader.store';
import DateTimeRangePicker from '@/components/DateTimeRangePicker.vue';
import Tooltip from '@/components/Tooltip.vue';
import AppLoader from '@/components/AppLoader.vue';
import topicsRoutes from '@/router/topics.routes';
import useConfirmBeforeAction from '@/composables/useConfirmBeforeAction';

const route = useRoute();
const router = useRouter();

const topicsStore = useTopicsStore();
const defaultTopic = ref({});

const messagesPageSize = ref(0);
const messagesPage = ref(0);

const messages = ref([]);
const topicLoading = ref(false);
const messagesLoading = ref(false);
const configLoading = ref(false);
const openSortDropdown = ref(false);
const showChart = ref(true);
const sortingSelected = ref('asc');
const lastOffset = ref(null);
const firstEverOffset = ref(null);
const paginationDirection = ref(null);
const showFilterModal = ref(false);
const saveLoading = ref(false);

const topicFilter = ref({
    regex: {
        fields: [], // or prepopulate with default fields, e.g. ['key']
        pattern: ''
    },
    seek: {
        seekType: 'offset', // valid values: 'none', 'offset', 'timestamp'
        seekValue: null
    }
});

const newPartitionCount = ref(null);

const topic = computed(() => topicsStore.all?.find((t) => t.id === route.params.topic_id));

const sortingOptions = ref([
    {
        name: 'Newest First',
        value: 'desc'
    },
    {
        name: 'Oldest First',
        value: 'asc'
    }
]);

const loaderStore = useLoaderStore();

const getFormattedValue = (metricKey, value) => {
    const metricInfo = getMetricFormattingInfo(metricKey, topicsStore.configs?.metrics);

    return formatter(value, metricInfo || {}, false);
};

const topicMetricsLoading = computed(() => loaderStore.isLoading('metrics.topics.fetch.table'));

const partitionTotal = computed(() => {
    const found = getFormattedValue('partitionTotal', topic.value?.metrics?.['partitionTotal']);

    return {
        metric: 'partitionTotal',
        displayValue: found.display,
        raw: found.raw
    };
});

let tabs = [
    { name: 'Status', href: '#', current: true },
    { name: 'Settings', href: '#', current: false }
];

let currentTab = ref('');

const setCurrentTab = (str) => {
    currentTab.value = str;

    router.push({
        query: { tab: currentTab.value }
    });
};

const toggleDropdown = () => {
    openSortDropdown.value = !openSortDropdown.value;
};

const chartOptions = ref({
    chart: {
        type: 'column',
        height: '300px'
    },
    title: {
        text: 'Message Volume Over Time'
    },
    xAxis: {
        categories: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    },
    yAxis: {
        min: 0,
        title: {
            text: 'Volume'
        }
    },
    tooltip: {
        headerFormat: '<span style="font-size:10px">{point.key}</span><table>',
        pointFormat: '<tr><td style="padding:0"><b>{point.y:.1f} messages</b></td></tr>',
        footerFormat: '</table>',
        shared: true,
        useHTML: true
    },
    plotOptions: {
        column: {
            pointPadding: 0.2,
            borderWidth: 0,
            dataLabels: {
                enabled: true
            }
        }
    },
    series: [
        {
            name: 'Messages',
            data: [49.9, 71.5, 106.4, 129.2, 144.0, 176.0, 135.6, 148.5, 216.4, 194.1, 95.6, 54.4], // Mock data
            borderRadiusTopLeft: '15%',
            borderRadiusTopRight: '15%',
            color: '#958cee'
        }
    ]
});

let checkedFilters = ref([]);

const filters = [
    {
        title: 'Messages per page',
        name: 'count',
        defaultIndex: 0,
        options: [
            { value: 10, displayValue: '10/page' },
            { value: 20, displayValue: '20/page' },
            { value: 50, displayValue: '50/page' }
        ]
    }
];

const stats = computed(() => {
    const [volume, errors, eventsSent, written] = ['byteTotal', 'recordErrorTotal', 'recordSendTotal', 'recordWrittenTotal'].map((metricKey) => {
        return {
            metric: metricKey,
            displayValue: getFormattedValue(metricKey, topic.value?.metrics?.[metricKey]).display
        };
    });

    return [
        {
            id: 3,
            name: 'Written',
            stat: written.displayValue,
            icon: QueueListIcon,
            colorClass: 'bg-blue-400'
        },
        {
            id: 0,
            name: 'Volume',
            stat: volume.displayValue,
            icon: ScaleIcon,
            colorClass: 'bg-purple-400'
        },
        {
            id: 1,
            name: 'Errors',
            stat: errors.displayValue,
            icon: ExclamationCircleIcon,
            colorClass: 'bg-red-500'
        },
        {
            id: 2,
            name: 'Events',
            stat: eventsSent.displayValue,
            icon: ChevronDoubleLeftIcon,
            colorClass: 'bg-green-500'
        }
    ];
});

function initializeTopicMessagesFilters() {
    filters.forEach((filter) => {
        if (filter.name === 'count') {
            messagesPageSize.value = filter.options[filter.defaultIndex].value;
        }
        checkedFilters.value[filter.name] = [filter.multiple ? this.CONSTANTS.all : filter.options[filter.defaultIndex]];
    });
}

async function onMessagesFilterToggle({ filter, filterOption }) {
    try {
        let pageSize = topicsStore.messagesPageSize;

        if (filter.name === 'count') {
            pageSize = filterOption.value;
        }

        messagesPage.value = 0;

        await fetchTopicsMessages(messagesPage.value, pageSize, null, null);
    } catch (error) {
        console.log(error);
    }
}

async function fetchTopicsMessages(newPage = null, newPageSize = null, direction = null, sort = null) {
    messagesLoading.value = true;
    try {
        messagesPage.value = _.isNil(newPage) ? messagesPage.value : newPage;
        messagesPageSize.value = _.isNil(newPageSize) ? messagesPageSize.value : newPageSize;
        sortingSelected.value = _.isNil(sort) ? sortingSelected.value || 'asc' : sort;
        paginationDirection.value = _.isNil(direction) ? paginationDirection.value : direction;

        const query = {
            page_size: messagesPageSize.value,
            page: messagesPage.value,
            sort: sortingSelected.value,
            direction: paginationDirection.value
        };

        // If regex filter is applied (non-empty pattern), include regex parameters.
        if (topicFilter.value.regex && topicFilter.value.regex?.pattern && topicFilter.value.regex?.pattern?.trim() !== '') {
            query.regex = topicFilter.value.regex.pattern;
            query.regex_search_fields = topicFilter.value.regex.fields;
            if (topicFilter.value.seek && topicFilter.value?.seek?.seekType && topicFilter.value?.seek?.seekValue !== null) {
                query.seek_type = topicFilter.value.seek.seekType;
                query.seek_value = topicFilter.value.seek.seekValue;
            }
        }
        // Otherwise, if only a seek filter is provided.
        else if (topicFilter.value?.seek && topicFilter.value?.seek?.seekType && topicFilter.value?.seek?.seekValue !== null) {
            query.seek_type = topicFilter.value.seek.seekType;
            query.seek_value = topicFilter.value.seek.seekValue;
        }

        const res = await topicsStore.fetchTopicMessages(route.params.topic_id, query);
        messages.value = res;
        messagesLoading.value = false;
        return res;
    } catch (error) {
        messagesLoading.value = false;
        console.error(error);
    }
}

const openFilterModal = () => {
    showFilterModal.value = true;
};

const applyFilter = (filterData) => {
    topicFilter.value = filterData;
    showFilterModal.value = false;
    messagesPage.value = 0;
    lastOffset.value = null;
    firstEverOffset.value = null;
    paginationDirection.value = null;

    fetchTopicsMessages();
};

async function handleDateFilterInput(filter) {
    console.log(filter);
}

const selectedMessage = ref(null);

const searchQuery = ref('');
const pageSize = ref(0);

const selectMessage = (message) => {
    selectedMessage.value = isRef(message) ? message?.value : message;
};

const removeFilter = () => {
    topicFilter.value = {
        regex: { fields: [], pattern: '' },
        seek: { seekType: null, seekValue: null }
    };

    messagesPage.value = 0;
    lastOffset.value = null;
    firstEverOffset.value = null;
    paginationDirection.value = null;

    fetchTopicsMessages();
};

watch(
    () => partitionTotal.value,
    (newValue) => {
        newPartitionCount.value = Number(newValue.raw);
    },
    { immediate: true }
);

onMounted(async () => {
    topicLoading.value = true;
    messagesLoading.value = true;
    configLoading.value = true;

    setCurrentTab(route.query.tab || 'Status');

    initializeTopicMessagesFilters();

    await Promise.all([
        topicsStore.getById(route.params.topic_id).then(
            (res) => {
                defaultTopic.value = _.cloneDeep(res);

                topicLoading.value = false;

                return res;
            },
            (err) => {
                topicLoading.value = false;
                console.error(err);
            }
        ),
        fetchTopicsMessages(),
        topicsStore.fetchTopicConfigs().then(
            (res) => {
                configLoading.value = false;
                return res;
            },
            (err) => {
                console.error(err);
            }
        )
    ]);
});

// const canSave = computed(() => {
//     return !_.isEqual(topic.value, defaultTopic.value);
// });

function canShowCancelSnapshot() {
    /* TBD */
    return false;
}

async function onSnapshot() {
    console.log('snapshotting');
}

async function onCancelSnapshot() {
    console.log('cancel snapshotting');
}

async function onChangeHashColumnState(column) {
    console.log('hashing column', column);
}

async function onChangeExcludeColumnState(column) {
    console.log('changing exclude column state', column);
}

async function saveTopic() {
    useConfirmBeforeAction(
        'save',
        async () => {
            saveLoading.value = true;
            try {
                await topicsStore.update(route.params.topic_id, {
                    topic_db_ids: topic.value.entity.topic_db_ids,
                    payload: {
                        partition_count: newPartitionCount.value
                    }
                });
            } catch (error) {
                toast.error('Failed to update the topic.');
            } finally {
                saveLoading.value = false;
            }
        },
        { actionName: 'update', name: topic.value.name },
        () => {}
    );
}

async function onDiscard() {
    newPartitionCount.value = Number(partitionTotal.value.raw);
}

/**
 * Prompt and delete this topic, then navigate back to topics list.
 */
async function onDeleteTopic(entity, name, id) {
    try {
        useConfirmBeforeAction(
            'delete',
            async () => {
                await topicsStore.delete(id);
                router.push({ name: 'topics' });
            },
            { entity, name }
        );
    } catch {
        // ignore cancellation
    }
}

const filterTooltipText = computed(() => {
    let message = '';
    const regexExists = topicFilter.value.regex?.pattern && topicFilter.value.regex?.pattern?.trim() !== '';

    if (regexExists) {
        message += `<b>Regex (${topicFilter.value.regex.fields?.join(', ')}):</b> ${topicFilter.value.regex.pattern}`;
    }

    if (topicFilter.value?.seek?.seekType && topicFilter.value?.seek?.seekValue !== null) {
        message += `${regexExists ? '<br>' : ''}<b>Seek (${topicFilter.value.seek.seekType}):</b> ${topicFilter.value.seek.seekType === 'timestamp' ? new Date(topicFilter.value.seek.seekValue).toUTCString() : topicFilter.value.seek.seekValue}`;
    }

    return message;
});
</script>

<style scoped>
/* Tailwind CSS styling */
</style>
